import CloseIcon from '@mui/icons-material/Close'
import {
  Box,
  Button,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Typography
} from '@mui/material'
import {
  Company,
  CompanyType,
  CompanyUsersStatus
} from '@shared/types/companies.types'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import EmptyListCircle from '../../assets/images/EmptyListCircle.svg'
import CompanyCard from '../../components/CompanyCard'
import EmptySearchResults from '../../components/EmptySearchResults'
import SearchInput from '../../components/forms/SearchInput'
import CardLoader from '../../components/loaders/CardLoader'
import { useCompaniesDirectory } from '../../hooks/useQueries/useCompanies'
import { OrderByOptions } from '../../types/form.types'

export const CompaniesDirectory = () => {
  const { t } = useTranslation()
  const [searchText, setSearchText] = useState('')
  const [companyType, setCompanyType] = useState('')
  const [sortBy, setSortBy] = useState('')
  const [lastAppliedFilters, setLastAppliedFilters] = useState({
    searchText: '',
    companyType: '',
    sortBy: ''
  })
  const [filteredCompanies, setFilteredCompanies] = useState<
    (Company & { status: CompanyUsersStatus | null })[]
  >([])
  const hasAppliedFilters = searchText || companyType || sortBy

  const { data: companies = [], isLoading } = useCompaniesDirectory()

  useEffect(() => {
    if (companies.length > 0) {
      setFilteredCompanies(companies)
    }
  }, [companies])

  const handleFilter = (
    searchValue: string,
    companyTypeValue: string,
    sortByValue: string
  ) => {
    let filtered = [...companies]

    if (searchValue) {
      filtered = filtered.filter(company =>
        JSON.stringify(company)
          .toLowerCase()
          .includes(searchValue.toLowerCase())
      )
    }

    if (companyTypeValue) {
      filtered = filtered.filter(company => company.type === companyTypeValue)
    }

    if (sortByValue) {
      filtered.sort((a, b) => {
        switch (sortByValue) {
          case OrderByOptions.NAME_ASC:
            return a.name.localeCompare(b.name)
          case OrderByOptions.NAME_DESC:
            return b.name.localeCompare(a.name)
          case OrderByOptions.CREATED_AT_ASC:
            return (
              (a.createdAt ? new Date(a.createdAt).getTime() : 0) -
              (b.createdAt ? new Date(b.createdAt).getTime() : 0)
            )
          case OrderByOptions.CREATED_AT_DESC:
            return (
              (b.createdAt ? new Date(b.createdAt).getTime() : 0) -
              (a.createdAt ? new Date(a.createdAt).getTime() : 0)
            )
          default:
            return 0
        }
      })
    }

    setFilteredCompanies(filtered)
    setLastAppliedFilters({
      searchText: searchValue,
      companyType: companyTypeValue,
      sortBy: sortByValue
    })
  }

  const areFiltersEqual = (
    current: typeof lastAppliedFilters,
    last: typeof lastAppliedFilters
  ) => {
    return (
      current.searchText === last.searchText &&
      current.companyType === last.companyType &&
      current.sortBy === last.sortBy
    )
  }

  const handleSelectChange = (event: SelectChangeEvent) => {
    setCompanyType(event.target.value as string)
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem',
        width: '100%',
        height: '47.625rem',
        borderRadius: '1rem',
        padding: '2rem 3rem',
        border: '1px solid #00897B',
        background:
          'linear-gradient(156deg, #1B5A4B -21.63%, #171B1E 42.81%), #171B1E',
        boxShadow:
          '0 4px 18px 3px rgba(0, 0, 0, 0.12), 0 10px 14px 1px rgba(0, 0, 0, 0.14), 0 6px 6px -3px rgba(0, 0, 0, 0.20)'
      }}
    >
      <Typography variant='h5'>{t('companies_directory.title')}</Typography>
      {!isLoading && companies.length === 0 ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '1rem'
          }}
        >
          <img
            src={EmptyListCircle}
            alt='Empty List'
            style={{
              width: '12.5rem',
              height: '12.5rem',
              objectFit: 'contain'
            }}
          />
          <Typography variant='h6'>
            {t('companies_directory.empty.title')}
          </Typography>
          <Typography variant='body1'>
            {t('companies_directory.empty.description')}
          </Typography>
        </Box>
      ) : (
        <>
          <Stack
            direction='row'
            justifyContent='space-between'
            padding='1rem 1rem'
          >
            <Stack direction='row' spacing='1rem'>
              <SearchInput
                searchText={searchText}
                setSearchText={setSearchText}
                handleFilter={handleFilter}
                additionalParams={[companyType, sortBy]}
              />
              <Select
                value={companyType}
                onChange={handleSelectChange}
                sx={{
                  width: '13.75rem',
                  '& .MuiSelect-select': {
                    padding: '.75rem .75rem'
                  }
                }}
                displayEmpty
                renderValue={selected => {
                  if (selected === '') {
                    return (
                      <Typography color='text.secondary'>
                        {t('common.company_type')}
                      </Typography>
                    )
                  }
                  return selected === CompanyType.CONSTRUCTOR
                    ? t('common.constructor_type')
                    : t('common.provider')
                }}
              >
                <MenuItem value={CompanyType.CONSTRUCTOR}>
                  {t('common.constructor_type')}
                </MenuItem>
                <MenuItem value={CompanyType.PROVIDER}>
                  {t('common.provider')}
                </MenuItem>
              </Select>
              <Select
                value={sortBy}
                onChange={e => setSortBy(e.target.value)}
                sx={{
                  width: '11.25rem',
                  '& .MuiSelect-select': {
                    padding: '.75rem .75rem'
                  }
                }}
                displayEmpty
                renderValue={selected => {
                  if (selected === '') {
                    return (
                      <Typography color='text.secondary'>
                        {t('companies_directory.order_by.label')}
                      </Typography>
                    )
                  }
                  return t(
                    `companies_directory.order_by.${selected.toLowerCase()}`
                  )
                }}
              >
                <MenuItem value={OrderByOptions.NAME_ASC}>
                  {t('companies_directory.order_by.name_asc')}
                </MenuItem>
                <MenuItem value={OrderByOptions.NAME_DESC}>
                  {t('companies_directory.order_by.name_desc')}
                </MenuItem>
                <MenuItem value={OrderByOptions.CREATED_AT_DESC}>
                  {t('companies_directory.order_by.created_at_desc')}
                </MenuItem>
                <MenuItem value={OrderByOptions.CREATED_AT_ASC}>
                  {t('companies_directory.order_by.created_at_asc')}
                </MenuItem>
              </Select>
              <Button
                variant='text'
                size='small'
                color='primary'
                onClick={() => handleFilter(searchText, companyType, sortBy)}
                disabled={
                  (!searchText && !companyType && !sortBy) ||
                  areFiltersEqual(
                    { searchText, companyType, sortBy },
                    lastAppliedFilters
                  )
                }
                sx={{
                  textTransform: 'capitalize'
                }}
              >
                {t('common.apply_filters')}
              </Button>
              {hasAppliedFilters && (
                <Button
                  sx={{ color: 'secondary.main', textTransform: 'none' }}
                  startIcon={<CloseIcon />}
                  onClick={() => {
                    setSearchText('')
                    setCompanyType('')
                    setSortBy('')
                    setFilteredCompanies(companies)
                    setLastAppliedFilters({
                      searchText: '',
                      companyType: '',
                      sortBy: ''
                    })
                  }}
                >
                  {t('common.delete_filters')}
                </Button>
              )}
            </Stack>
          </Stack>
          {isLoading ? (
            <Stack direction='row' spacing='1.5rem'>
              {Array.from({ length: 3 }).map((_, index) => (
                <CardLoader
                  key={index}
                  sx={{
                    width: '18rem',
                    borderRadius: '.25rem',
                    height: '24rem'
                  }}
                />
              ))}
            </Stack>
          ) : filteredCompanies.length === 0 && hasAppliedFilters ? (
            <EmptySearchResults />
          ) : (
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '1.5rem',
                maxWidth: '100%',
                overflowY: 'auto',
                alignContent: 'flex-start',
                padding: '0 1rem'
              }}
            >
              {filteredCompanies?.map(
                (company: Company & { status: CompanyUsersStatus | null }) => (
                  <CompanyCard key={company.id} company={company} />
                )
              )}
            </Box>
          )}
        </>
      )}
    </Box>
  )
}
